import { useContext, useState } from 'react';
import { styled } from 'styled-components';

import * as api from '../../api';

import { ConfigurationContext } from '../../context/configurationContext';
import ChatInput from '../Chat/Input';
import ConfigurableIcon from '../Chat/ConfigurableIcon';

interface Props {
  onInputSubmit: (value: string) => void;
  compact?: boolean;
}

const DEFAULT_PROMPTS = [
  JSON.stringify({
    text: 'What can the assistant help with and how does it work?',
    icon: api.OrganizationAssistantConfiguration.AvailableIcon.Help,
  }),
  JSON.stringify({
    text: 'How can I pay my bill and is financial assistance available?',
    icon: api.OrganizationAssistantConfiguration.AvailableIcon.Payment,
  }),
  JSON.stringify({
    text: 'Where is the cardiology clinic and do they have parking?',
    icon: api.OrganizationAssistantConfiguration.AvailableIcon.Activity,
  }),
  JSON.stringify({
    text: 'How long do I need to fast before my upcoming test?',
    icon: api.OrganizationAssistantConfiguration.AvailableIcon.Clock,
  }),
  JSON.stringify({
    text: 'What is the status of my prior authorization?',
    icon: api.OrganizationAssistantConfiguration.AvailableIcon.Check,
  }),
  JSON.stringify({
    text: '我如何支付账单，是否有可用的经济援助?',
    icon: api.OrganizationAssistantConfiguration.AvailableIcon.Star,
  }),
];

const HeroPrompt: React.FC<Props> = ({ onInputSubmit, compact }) => {
  const [inputValue, setInputValue] = useState('');
  const context = useContext(ConfigurationContext);

  const handleOnSubmit = () => {
    onInputSubmit(inputValue);
    setInputValue('');
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && event.shiftKey === false) {
      event.preventDefault();
      handleOnSubmit();
    }
  };

  return (
    <Container
      $transparentBackground={context?.transparentHeroBackgroundEnabled}
    >
      <BodyContainer>
        <ContentContainer className="container">
          {context?.eyebrowContent ? (
            <Eyebrow>{context.eyebrowContent}</Eyebrow>
          ) : null}
          <GreetingMessage $compact={compact}>
            {context?.greetingMessage ?? 'How can I assist you today?'}
          </GreetingMessage>
          <ActionContainer>
            <ChatInput
              value={inputValue}
              onChange={(newValue) => setInputValue(newValue)}
              onKeyDown={handleOnKeyDown}
              onSubmit={() => {
                handleOnSubmit();
              }}
              glowEnabled={true}
            />
            <Hint $compact={compact}>
              {context?.inputHelperText ??
                'If you are experiencing an emergency, call 9-1-1 or visit your nearest emergency room.'}
            </Hint>
          </ActionContainer>
          {!compact && (
            <>
              <Prompt>
                {context?.heroCommonQuestionsHeader ?? 'Common questions'}
              </Prompt>
              <QuestionList>
                {(context?.promptQuestions?.length
                  ? context?.promptQuestions
                  : DEFAULT_PROMPTS
                ).map((question) => {
                  const parsedQuestion: {
                    text: string;
                    icon?: api.OrganizationAssistantConfiguration.AvailableIcon;
                  } = JSON.parse(question);
                  return (
                    <QuestionExample
                      key={parsedQuestion.text}
                      onClick={() => onInputSubmit(parsedQuestion.text)}
                    >
                      {parsedQuestion.icon ? (
                        <ConfigurableIcon iconName={parsedQuestion.icon} />
                      ) : (
                        <span></span>
                      )}
                      <span>{parsedQuestion.text}</span>
                    </QuestionExample>
                  );
                })}
              </QuestionList>
            </>
          )}
        </ContentContainer>
      </BodyContainer>
    </Container>
  );
};

const Container = styled.div.attrs<{ $transparentBackground?: boolean }>(
  (props) => ({
    $transparentBackground: props.$transparentBackground ?? false,
  }),
)`
  width: 100%;
  background-color: ${(props) =>
    props.$transparentBackground ? 'transparent' : 'var(--background-color)'};
  padding-bottom: 30px;
`;

const BodyContainer = styled.div`
  padding-top: 64px;
  padding-bottom: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionContainer = styled.div`
  margin: 40px 0;
`;

const ContentContainer = styled.div`
  gap: 0;
`;

const QuestionExample = styled.li`
  width: calc(50% - 8px);
  cursor: pointer;
  color: var(--common-question-text-color);
  font-size: 16px;
  list-style: none;
  background-color: var(--common-question-background-color);
  border-radius: 5px;
  padding-left: 20px;
  padding-top: 15px;
  padding-bottom: 15px;
  padding-right: 20px;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: flex-start;
  font-family: var(--paragraph-font-family) !important;

  @media (max-width: 680px) {
    width: 100%;
  }

  svg {
    overflow: visible;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const GreetingMessage = styled.h1.attrs<{ $compact?: boolean }>((props) => ({
  $compact: props.$compact ?? false,
}))`
  text-align: ${(props) => (props.$compact ? 'left' : 'center')};
  font-size: var(--greeting-font-size-desktop);
  color: var(--greeting-color);
  font-family: var(--greeting-font-family);
  font-weight: var(--greeting-font-weight);
  margin-bottom: 0;

  @media (max-width: 680px) {
    font-size: var(--greeting-font-size-mobile);
  }
`;

const Prompt = styled.div`
  margin-bottom: 24px;
  word-break: break-word;
  font-size: 16px;
  font-weight: bold;
  color: var(--common-questions-header-color);
`;

const QuestionList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-left: 0;
`;

const Hint = styled.p.attrs<{ $compact?: boolean }>((props) => ({
  $compact: props.$compact ?? false,
}))`
  margin-top: 1rem;
  font-size: 12px;
  color: var(--input-helper-text-color);
  text-align: ${(props) => (props.$compact ? 'left' : 'center')};
  margin-bottom: 0;

  @media (max-width: 680px) {
    text-align: left;
  }
`;

const Eyebrow = styled.div`
  text-align: center;
  font-size: var(--eyebrow-font-size-desktop);
  font-family: var(--eyebrow-font-family);

  @media (max-width: 680px) {
    font-size: var(--eyebrow-font-size-mobile);
  }
`;

export default HeroPrompt;
